// QuoteView.jsx — BM-H77 Phase-2 read-only quote view + state actions.
//
// Route: /quotes/:ref. Two-column layout — left shows the summary block,
// line items, and totals; the sticky right sidebar surfaces the action
// buttons appropriate for the current status. Adding ?send=1 to the URL
// opens the Send-flow modal on mount so QuoteBuilder can "Save and send"
// straight through.

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Send, FileText, CheckCircle, XCircle, ExternalLink,
  Copy, RefreshCw, Undo2, ShieldAlert, Download,
} from "lucide-react";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { deriveRowState } from "./Quotes";

function authHeaders() {
  const uid = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  const email = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || uid;
  return { "X-Bonsai-User-Id": uid || email, "X-User-Email": email };
}

function fmtGbp(pounds) {
  const n = Number(pounds);
  if (!Number.isFinite(n)) return "£0";
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    // Pence only when there are pence (the £12.50 bank transfer fees).
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  }).format(n);
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return "";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function SendModal({ open, onClose, outlookLink }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
      data-testid="quote-view-send-modal">
      <div className="bg-navy-card border border-white/10 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-3">
          <Send className="w-5 h-5 text-teal" />
          <h2 className="text-lg font-bold text-white">Quote drafted in Outlook</h2>
        </div>
        <p className="text-sm text-gray-300 mb-5">
          Your quote is drafted in Outlook. Click below to review and send.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}
            data-testid="quote-view-send-modal-close"
            className="text-gray-400 hover:text-white">
            Close
          </Button>
          {outlookLink && (
            <a href={outlookLink} target="_blank" rel="noopener noreferrer"
              data-testid="quote-view-send-modal-open-outlook"
              className="inline-flex items-center gap-1.5 bg-teal text-black hover:bg-teal-hover px-4 py-2 rounded text-sm font-medium">
              <ExternalLink className="w-4 h-4" /> Open in Outlook &rarr;
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function QuoteView() {
  const navigate = useNavigate();
  const { ref } = useParams();
  const [searchParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(false);
  const [sendModal, setSendModal] = useState({ open: false, link: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/hub/quotes/${ref}`, { headers: authHeaders() });
      setQuote(r.data);
    } catch (err) {
      setError(errorMessage(err, "Failed to load quote."));
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => { load(); }, [load]);

  // Auto-open send flow when arriving from the "Save and send" builder path.
  useEffect(() => {
    if (!quote) return;
    if (searchParams.get("send") === "1" && quote.status === "draft" && !acting) {
      doSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote]);

  async function doSend() {
    if (acting || !quote) return;
    setActing(true);
    try {
      const r = await axios.post(`${API}/hub/quotes/${ref}/send`, {
        to_email: quote.client?.email || "",
      }, { headers: authHeaders() });
      setQuote(r.data);
      const link = r.data?.sent?.outlook_web_link || "";
      setSendModal({ open: true, link });
    } catch (err) {
      toast.error(errorMessage(err, "Failed to send quote."));
    } finally {
      setActing(false);
    }
  }

  async function doMarkAccepted(undo = false) {
    if (acting) return;
    setActing(true);
    try {
      await axios.post(`${API}/hub/quotes/${ref}/mark-accepted`,
        { undo, accepted_via: "manual" }, { headers: authHeaders() });
      toast.success(undo ? "Accepted state cleared."
        : "Quote marked accepted. Next step: run conflict check.");
      await load();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to update accepted state."));
    } finally {
      setActing(false);
    }
  }

  async function doDecline() {
    const reason = window.prompt("Reason for decline?");
    if (reason === null) return;
    setActing(true);
    try {
      await axios.post(`${API}/hub/quotes/${ref}/decline`,
        { reason: reason || "" }, { headers: authHeaders() });
      toast.success("Quote declined.");
      await load();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to decline quote."));
    } finally {
      setActing(false);
    }
  }

  function goToConflictCheck() {
    if (!quote) return;
    const client = quote.client || {};
    const feInitials = quote.fee_earner?.initials || "";
    const params = new URLSearchParams();
    params.set("source_quote_ref", quote.quote_ref);
    params.set("client_name",
      client.name || [client.first_name, client.last_name].filter(Boolean).join(" "));
    params.set("client_email", client.email || "");
    // BM-H615: this now reaches the onboarding form, whose description is
    // shown to the client, so no em dash.
    params.set("matter_description",
      (quote.transaction_type || "").replace(/_/g, " ") + ", " + (quote.property?.address || ""));
    params.set("fee_earner_initials", feInitials);
    params.set("estimated_fee", String(quote.totals?.fees_ex_vat || 0));
    navigate(`/conflict-search?${params.toString()}`);
  }

  function copyShareableLink() {
    const link = `${window.location.origin}/quotes/${ref}`;
    navigator.clipboard?.writeText(link);
    toast.success("Shareable link copied.");
  }

  // BM-H128 -- clone the current quote into a fresh draft (new quote_ref)
  // so a mistake on a sent/accepted/declined/expired quote can be fixed
  // without mutating what the client already saw. Converted quotes are
  // rejected server-side with a 409.
  async function doRevise() {
    setActing(true);
    try {
      const r = await axios.post(`${API}/hub/quotes/${ref}/revise`,
        {}, { headers: authHeaders() });
      const newRef = r?.data?.quote_ref;
      if (!newRef) throw new Error("No quote_ref returned from revise.");
      toast.success(`Revised as ${newRef}. Opening editor...`);
      navigate(`/quotes/${newRef}/edit`);
    } catch (err) {
      const detail = errorMessage(err, "Failed to revise quote.");
      toast.error(typeof detail === "string" ? detail : "Failed to revise quote.");
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-navy p-6 text-white flex items-center justify-center"
      data-testid="quote-view-loading">
      <Loader2 className="w-6 h-6 animate-spin text-teal" />
    </div>;
  }
  if (error || !quote) {
    return <div className="min-h-screen bg-navy p-6 text-white" data-testid="quote-view-error">
      <div className="max-w-lg mx-auto bg-navy-card border border-status-red/40 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-3">
          <ShieldAlert className="w-6 h-6 text-status-red" />
          <h1 className="text-lg font-bold">Quote not available</h1>
        </div>
        <p className="text-sm text-gray-300">{error || "The requested quote was not found."}</p>
        <Button variant="ghost" onClick={() => navigate("/quotes")}
          data-testid="quote-view-error-back"
          className="mt-4 text-gray-300">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Quotes
        </Button>
      </div>
    </div>;
  }

  const client = quote.client || {};
  const property = quote.property || {};
  const totals = quote.totals || {};
  const lineItems = quote.line_items || {};
  const state = deriveRowState(quote);
  const status = quote.status;

  return (
    <div className="min-h-screen bg-navy p-6 text-white" data-testid="quote-view-page">
      <SendModal open={sendModal.open}
        onClose={() => setSendModal({ open: false, link: "" })}
        outlookLink={sendModal.link} />
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" onClick={() => navigate("/quotes")}
            data-testid="quote-view-back"
            className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" /> Quotes
          </Button>
          <FileText className="w-5 h-5 text-teal" />
          <h1 className="text-xl font-bold" data-testid="quote-view-ref">{quote.quote_ref}</h1>
          <span data-testid={`quote-view-status-${status}`}
            className="ml-2 px-2 py-0.5 rounded-full text-xs bg-teal/15 text-teal capitalize">
            {status}
          </span>
          {/* BM-H85 — loud TEST chip so nobody sends a training quote by accident. */}
          {quote.is_test && (
            <span data-testid="quote-view-test-chip"
              className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40 uppercase tracking-wide">
              Test
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left column — summary */}
          <div className="lg:col-span-2 space-y-4">
            <section className="bg-navy-card border border-white/10 rounded-lg p-4"
              data-testid="quote-view-summary">
              <h2 className="text-sm font-bold text-teal mb-3">Summary</h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-2 text-sm">
                <dt className="text-gray-400">Client</dt>
                <dd className="text-white">
                  {client.name || [client.first_name, client.last_name].filter(Boolean).join(" ")}
                  <div className="text-xs text-gray-500">{client.email}</div>
                </dd>
                <dt className="text-gray-400">Transaction</dt>
                <dd className="text-white capitalize">{(quote.transaction_type || "").replace(/_/g, " ")}</dd>
                <dt className="text-gray-400">Property</dt>
                <dd className="text-white">{property.address}</dd>
                <dt className="text-gray-400">Fee earner</dt>
                <dd className="text-white">{quote.fee_earner?.full_name || quote.fee_earner?.email}</dd>
                <dt className="text-gray-400">Created</dt>
                <dd className="text-white">{fmtDateTime(quote.created_at)}</dd>
                {quote.sent?.sent_at && (<>
                  <dt className="text-gray-400">Sent</dt>
                  <dd className="text-white">{fmtDateTime(quote.sent.sent_at)}</dd>
                </>)}
                {quote.accepted_at && (<>
                  <dt className="text-gray-400">Accepted</dt>
                  <dd className="text-teal">{fmtDateTime(quote.accepted_at)}</dd>
                </>)}
              </dl>
            </section>

            <section className="bg-navy-card border border-white/10 rounded-lg p-4"
              data-testid="quote-view-line-items">
              <h2 className="text-sm font-bold text-teal mb-3">Line items</h2>
              <table className="w-full text-sm">
                <tbody>
                  {lineItems.base_fee && (
                    <tr className="border-b border-white/5">
                      <td className="py-1.5 text-gray-300">{lineItems.base_fee.description || "Base fee"}</td>
                      <td className="py-1.5 text-right text-white">{fmtGbp(lineItems.base_fee.amount || 0)}</td>
                    </tr>
                  )}
                  {(lineItems.additional || []).map((li, i) => (
                    <tr key={`add-${i}`} className="border-b border-white/5">
                      <td className="py-1.5 text-gray-300">{li.description}</td>
                      <td className="py-1.5 text-right text-white">{fmtGbp(li.amount)}</td>
                    </tr>
                  ))}
                  {(lineItems.manual || []).map((li, i) => (
                    <tr key={`man-${i}`} className="border-b border-white/5">
                      <td className="py-1.5 text-gray-300">{li.description}</td>
                      <td className="py-1.5 text-right text-white">{fmtGbp(li.amount)}</td>
                    </tr>
                  ))}
                  {/* BM-H128 — surface the discount that was applied (only when a discount is on the quote). */}
                  {Number(totals.discount_pct) > 0 && (
                    <tr className="border-b border-white/5" data-testid="quote-view-row-discount">
                      <td className="py-1.5 text-gray-300">Discount ({totals.discount_pct}%)</td>
                      <td className="py-1.5 text-right text-white">-{fmtGbp(totals.discount_amount)}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="py-1.5 text-gray-400">SDLT</td>
                    <td className="py-1.5 text-right text-white">{fmtGbp(totals.sdlt)}</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 text-gray-400">VAT</td>
                    <td className="py-1.5 text-right text-white">{fmtGbp(totals.vat_on_fees)}</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-2 text-teal font-bold">Total inc VAT</td>
                    <td className="py-2 text-right text-teal font-bold"
                      data-testid="quote-view-total">{fmtGbp(totals.grand_total)}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {(quote.notes_client || quote.notes_internal) && (
              <section className="bg-navy-card border border-white/10 rounded-lg p-4"
                data-testid="quote-view-notes">
                {quote.notes_client && (<>
                  <h3 className="text-xs font-bold text-gray-400 uppercase mb-1">Client notes</h3>
                  <p className="text-sm text-white mb-3 whitespace-pre-wrap">{quote.notes_client}</p>
                </>)}
                {quote.notes_internal && (<>
                  <h3 className="text-xs font-bold text-gray-400 uppercase mb-1">Internal notes</h3>
                  <p className="text-sm text-gray-400 whitespace-pre-wrap">{quote.notes_internal}</p>
                </>)}
              </section>
            )}
          </div>

          {/* Right column — sticky action sidebar */}
          <aside className="lg:sticky lg:top-6 h-fit space-y-2"
            data-testid="quote-view-actions">
            <a href={`${API}/hub/quotes/${ref}/pdf`} target="_blank" rel="noopener noreferrer"
              data-testid="quote-view-action-preview-pdf"
              className="w-full flex items-center gap-2 px-3 py-2 rounded border border-white/10 bg-navy-card text-sm text-gray-300 hover:bg-white/5">
              <Download className="w-4 h-4" /> Preview PDF
            </a>

            {status === "draft" && (
              <>
                <Button onClick={() => navigate(`/quotes/${ref}/edit`)}
                  data-testid="quote-view-action-edit"
                  className="w-full justify-start bg-navy-card border border-white/10 text-gray-300 hover:bg-white/5">
                  Edit
                </Button>
                <Button onClick={doSend} disabled={acting}
                  data-testid="quote-view-action-send"
                  className="w-full justify-start bg-teal text-black hover:bg-teal-hover">
                  {acting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  Send quote &rarr;
                </Button>
              </>
            )}

            {status === "sent" && !quote.accepted_at && (
              <>
                <Button onClick={() => doMarkAccepted(false)} disabled={acting}
                  data-testid="quote-view-action-mark-accepted"
                  className="w-full justify-start bg-teal/15 text-teal hover:bg-teal/25 border border-teal/30">
                  <CheckCircle className="w-4 h-4 mr-1" /> Mark accepted
                </Button>
                <Button onClick={doDecline} disabled={acting}
                  data-testid="quote-view-action-decline"
                  className="w-full justify-start bg-status-red/10 text-status-red hover:bg-status-red/20 border border-status-red/30">
                  <XCircle className="w-4 h-4 mr-1" /> Decline
                </Button>
                <Button onClick={copyShareableLink}
                  data-testid="quote-view-action-copy-link"
                  className="w-full justify-start bg-navy-card border border-white/10 text-gray-300 hover:bg-white/5">
                  <Copy className="w-4 h-4 mr-1" /> Copy shareable link
                </Button>
                {/* BM-H128 — sent quotes get a Revise option so mistakes can be corrected without mutating the original. */}
                <Button onClick={doRevise} disabled={acting}
                  data-testid="quote-view-action-revise"
                  className="w-full justify-start bg-navy-card border border-white/10 text-gray-300 hover:bg-white/5">
                  <RefreshCw className="w-4 h-4 mr-1" /> Revise as new draft
                </Button>
              </>
            )}

            {state === "accepted_no_check" && (
              <>
                <Button onClick={goToConflictCheck}
                  data-testid="quote-view-action-start-conflict-check"
                  className="w-full justify-start bg-status-amber/15 text-status-amber hover:bg-status-amber/25 border border-status-amber/30">
                  Start conflict check &rarr;
                </Button>
                <Button onClick={() => doMarkAccepted(true)} disabled={acting}
                  data-testid="quote-view-action-undo-accept"
                  className="w-full justify-start bg-navy-card border border-white/10 text-gray-300 hover:bg-white/5">
                  <Undo2 className="w-4 h-4 mr-1" /> Undo accept
                </Button>
                {/* BM-H128 — accepted (pre-check) quotes may also need revising if the fees are wrong. */}
                <Button onClick={doRevise} disabled={acting}
                  data-testid="quote-view-action-revise"
                  className="w-full justify-start bg-navy-card border border-white/10 text-gray-300 hover:bg-white/5">
                  <RefreshCw className="w-4 h-4 mr-1" /> Revise as new draft
                </Button>
              </>
            )}

            {state === "check_in_progress" && (
              <a href={`/conflict-check/${quote.conversion?.conflict_check_id}`}
                data-testid="quote-view-action-open-conflict-check"
                className="w-full flex items-center gap-2 px-3 py-2 rounded border border-teal/40 bg-teal/15 text-teal text-sm hover:bg-teal/25">
                <ExternalLink className="w-4 h-4" /> Open conflict check
              </a>
            )}

            {status === "converted" && (
              <a href={`/matter/${quote.conversion?.matter_id}`}
                data-testid="quote-view-action-open-matter"
                className="w-full flex items-center gap-2 px-3 py-2 rounded border border-teal/40 bg-teal/15 text-teal text-sm hover:bg-teal/25">
                <ExternalLink className="w-4 h-4" /> Open matter &rarr;
              </a>
            )}

            {(status === "declined" || status === "expired") && (
              // BM-H128 -- swap the broken ?reissue_from= flow (QuoteBuilder never read that query param)
              // for a real POST /revise, which clones the quote into a fresh draft and lands in the editor.
              <Button onClick={doRevise} disabled={acting}
                data-testid="quote-view-action-reissue"
                className="w-full justify-start bg-teal/15 text-teal hover:bg-teal/25 border border-teal/30">
                <RefreshCw className="w-4 h-4 mr-1" /> Reissue as new draft
              </Button>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
